import { useCallback, useMemo, useRef } from "react";
import { isAxiosError } from "axios";
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  actingTenantFor,
  effectiveAccessQueryOptions,
  SERVENGINE_LOGIN,
} from "@/api/access/access.queries";
import { SUPER_ADMIN } from "@/components/data/constants";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useRequestScope } from "@/hooks/useRequestScope";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import { addTemporaryRole, endTemporaryRole } from "@/api/access/temporaryRoles";
import { fetchUserEffectiveAccess, withCompanyReads } from "@/api/access/userEffective";
import { fetchScopeOptions, saveEntityScope, type ScopeWrite } from "@/api/access/userScope";
import type { UserScopeCompany } from "@/api/access/userScope.types";
import { PLATFORM_TENANT_ID } from "@/store/entityTenantStore";
import {
  grantReaches,
  type RoleGrant,
} from "@/components/features/user-management/UserRecord/UserRolesScopeTab/addRoleFlow";
import type {
  CategoryBox,
  ScopeValueNames,
} from "@/components/features/user-management/UserRecord/UserRolesScopeTab/userRolesScope";
import type { EffectiveAccess, HeldRole, RoleScopeKinds } from "@/api/access/access.types";
import { getUserScope } from "@/api/roles-permissions/roleHolders";
import { fetchRolesIn, isProtectedRole, type CompanyRole } from "@/api/roles-permissions/rolesIn";
import { changeUserRoles } from "@/api/roles-permissions/roleMembership";
import {
  fetchAccessCatalog,
  fetchRoleById,
  PermissionsQueryKeys,
} from "@/api/roles-permissions/roles-permissions.queries";
import type { RoleByIdResponse } from "@/api/roles-permissions/roles-permissions.queries.types";
import type { CustomAxiosRequestConfig } from "@/types/common.types";
import {
  fetchUserRecord,
  PersonNotFoundError,
  updateUserEntities,
} from "@/api/user-management/userRecord";
import { RoleHoldersQueryKeys } from "@/hooks/roles-permissions/useRoleHolders";
import { refreshPersonRecord } from "@/hooks/user-management/personRecordQueryKeys";

export const UserAccessQueryKeys = {
  effective: "userEffectiveAccess",
} as const;

/** Same key as the role Users tab's per-holder read, so a save here refreshes that tab too. */
export const useUserScope = (userId: string) =>
  useQuery({
    queryKey: [RoleHoldersQueryKeys.userScope, userId],
    queryFn: ({ signal }) => getUserScope(userId, signal),
    retry: false,
  });

/**
 * The person's companies as the widest view this login has: the scope read made without the
 * acting company, which narrows it to that company alone (backend `scopeService`, `companyInScope`).
 * A super admin's person read lists every company the person is in, acting or not (D134), so this
 * is what names them. With nothing narrowing the request it is the tab's own read.
 */
export const usePersonCompanies = (userId: string) => {
  const requestScope = useRequestScope();
  const { userType } = useActiveSession();
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  const acting = actingTenantFor(requestScope, { userType, isImpersonating }) !== null;
  return useQuery({
    queryKey: acting
      ? [RoleHoldersQueryKeys.userScope, userId, SERVENGINE_LOGIN]
      : [RoleHoldersQueryKeys.userScope, userId],
    queryFn: ({ signal }) =>
      acting ? getUserScope(userId, signal, PLATFORM_TENANT_ID) : getUserScope(userId, signal),
    retry: false,
  });
};

/** A company the reader can't see the person in answers 404; the ServEngine read's answer stands. */
const isNotInCompany = (error: unknown) => isAxiosError(error) && error.response?.status === 404;

export interface UserEffectiveAccessResult {
  data: EffectiveAccess | undefined;
  /** The ServEngine read's error first, so its 404 (no entity the reader can see) is told apart. */
  error: Error | null;
  isError: boolean;
  refetch: () => void;
}

/**
 * The person's access per company (D253, SERV-3576): the ServEngine read, with each company the
 * person is in replaced by that company's own read, the only login its entity-level roles apply
 * at. The companies are the scope read's, which admits them by the same rule as the ServEngine
 * read (backend `companyInScope`) and leaves the ServEngine company out.
 */
export const useUserEffectiveAccess = (userId: string): UserEffectiveAccessResult => {
  const scope = useUserScope(userId);
  const companyIds = useMemo(
    () => (scope.data ?? []).map((company) => company.tenantId),
    [scope.data],
  );
  const combineReads = useCallback(
    (results: UseQueryResult<EffectiveAccess>[]) => {
      const [servengine, ...companies] = results;
      const failed = results.filter(
        (result, index) => result.isError && (index === 0 || !isNotInCompany(result.error)),
      );
      const settled = results.every((result) => !result.isPending);
      return {
        data:
          servengine.data && settled && failed.length === 0
            ? withCompanyReads(
                servengine.data,
                companies.map((result, index) => ({
                  tenantId: companyIds[index],
                  access: result.data ?? null,
                })),
              )
            : undefined,
        error: failed[0]?.error ?? null,
        retry: () => failed.forEach((result) => void result.refetch()),
      };
    },
    [companyIds],
  );
  const reads = useQueries({
    queries: [SERVENGINE_LOGIN, ...companyIds].map((login) => ({
      queryKey: [UserAccessQueryKeys.effective, userId, login],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchUserEffectiveAccess(userId, login, signal),
      retry: false,
    })),
    combine: combineReads,
  });

  // Without the companies the ServEngine read alone would show the very "No entity yet" this
  // reads around, so nothing is answered until the scope read is in.
  const data = scope.data ? reads.data : undefined;
  const error = reads.error ?? scope.error;
  // An entity just added brings a new company read; the last answer stays meanwhile, so the tab
  // does not drop to a loader and unmount the modal that added it.
  const last = useRef<{ userId: string; data: EffectiveAccess } | null>(null);
  if (data) last.current = { userId, data };
  const kept = error === null && last.current?.userId === userId ? last.current.data : undefined;

  return {
    data: data ?? kept,
    error,
    isError: error !== null,
    refetch: () => {
      if (scope.isError) void scope.refetch();
      reads.retry();
    },
  };
};

/** Every permission a role could hold: the actions of every catalog entry. */
export const useCatalogPermissionTotal = () =>
  useQuery({
    queryKey: [PermissionsQueryKeys.getAccessCatalog],
    queryFn: fetchAccessCatalog,
    staleTime: 5 * 60 * 1000,
    select: (response) =>
      (response.data ?? []).reduce((sum, entry) => sum + entry.actions.length, 0),
  });

/**
 * Each held role's own record (the role page's cache key), by role id. An entity's own copy of a
 * role can't be read from a ServEngine login (404 on dev), so a role missing from the map is
 * unknown, not empty.
 */
const useHeldRoleRecords = (roles: HeldRole[]): Record<string, RoleByIdResponse["data"]> => {
  const roleIds = [...new Set(roles.map((role) => role.roleId))];
  const results = useQueries({
    queries: roleIds.map((roleId) => ({
      queryKey: [PermissionsQueryKeys.getRoleById, roleId],
      queryFn: () =>
        fetchRoleById(roleId, { suppressErrorAlert: true } as CustomAxiosRequestConfig),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const records: Record<string, RoleByIdResponse["data"]> = {};
  results.forEach((result, index) => {
    const role = result.data?.data;
    if (role) records[roleIds[index]] = role;
  });
  return records;
};

/** Which boxes each held role puts in play, by role id; a role not read is unknown, not "requires nothing". */
export const useHeldRoleScopeKinds = (roles: HeldRole[]): Record<string, RoleScopeKinds> => {
  const kinds: Record<string, RoleScopeKinds> = {};
  for (const [roleId, role] of Object.entries(useHeldRoleRecords(roles))) {
    if (role.scopeKinds) kinds[roleId] = role.scopeKinds;
  }
  return kinds;
};

/** Each held role's stored reach, by role id; a role not read is unknown, not "no reach". */
export const useHeldRoleReaches = (roles: HeldRole[]): Record<string, string | null> => {
  const reaches: Record<string, string | null> = {};
  for (const [roleId, role] of Object.entries(useHeldRoleRecords(roles))) {
    reaches[roleId] = role.reach ?? null;
  }
  return reaches;
};

/**
 * `removedRoleIds` only deletes rows with no end date, so a temporary role is ended through its
 * own route instead; sending it to the update would report a removal that never happened.
 */
export const useRemoveUserRole = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    // Acting in the role's own company: the routes look the role up there, so an entity's copy
    // is not found from ServEngine level or from another entity picked in the sidebar.
    mutationFn: (role: HeldRole) =>
      role.temporary
        ? endTemporaryRole(userId, role.userRoleId, role.roleTenantId)
        : changeUserRoles({
            userId,
            removedRoleIds: [role.roleId],
            actingEntityId: role.roleTenantId,
          }),
    onSuccess: () => refreshPersonRecord(queryClient, userId, "roles"),
  });
};

export const useSaveEntityScope = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ company, writes }: { company: UserScopeCompany; writes: ScopeWrite[] }) =>
      saveEntityScope(userId, company, writes),
    // Even a partial save changed something, so the tab re-reads either way.
    onSettled: () => refreshPersonRecord(queryClient, userId, "scope"),
  });
};

export type AddEntityInput = {
  entityId: string;
  company: UserScopeCompany;
  writes: ScopeWrite[];
  /** Set on a retry after the membership already landed, so only the scope is written. */
  membershipDone: boolean;
};

/**
 * Adds an entity: the membership first, then its scope, because the scope routes refuse a box
 * for a company the person is not in. The update takes `entityIds` as the person's whole list
 * and removes every entity left out, so the list is read fresh right before it is written; that
 * read's 404 is the person's, so it is rethrown as `PersonNotFoundError`, apart from the write's.
 * Resolves with what did not land once the membership exists, so the modal can retry the scope.
 */
export const useAddUserEntity = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ entityId, company, writes, membershipDone }: AddEntityInput) => {
      if (!membershipDone) {
        const record = await fetchUserRecord(userId).catch((error: unknown) => {
          if (isAxiosError(error) && error.response?.status === 404)
            throw new PersonNotFoundError();
          throw error;
        });
        const current = record.entityIds;
        if (!Array.isArray(current)) throw new Error("The person's entities could not be read");
        await updateUserEntities(userId, [...new Set([...current, entityId])]);
      }
      const scope = await saveEntityScope(userId, company, writes);
      return { failed: scope.failed.length };
    },
    onSettled: () => refreshPersonRecord(queryClient, userId, "roles"),
  });
};

/** One entity's own copy of a role, which is what a holder there is given. */
export type EntityRoleCopy = {
  entityId: string;
  entityName: string;
  roleId: string;
  scopeKinds: RoleScopeKinds | null;
};

/**
 * One role Add a Role offers, listed once (Jesus, 2026-10-29: "just a list of roles, then … I
 * select the entity"). A ServEngine-level role is given as itself; an entity role is given as the
 * chosen entity's own copy, so it applies there (ServEngine's copy of an entity role reaches no
 * entity, backend `engine.appliesIn`).
 */
export type GivableRole = {
  key: string;
  name: string;
  /** Set for a role given at ServEngine level. */
  roleId: string | null;
  level: CompanyRole["level"];
  scopeKinds: RoleScopeKinds | null;
  /** Set for an entity role: the person's entities that have it, in list order. */
  copies: EntityRoleCopy[];
};

const roleKey = (role: CompanyRole) => role.code ?? role.name.trim().toLowerCase();

/**
 * The roles Add a Role offers (sketch `add-role-modal`: "the person's entities plus
 * ServEngine-level"). Each entity's roles are read in that entity, the only place its copies are
 * listed.
 *
 * Only what the save accepts is offered (D263 (2); backend `assertRolesWithinEntity`): below super
 * admin, no ServEngine-level role, since the live copies there are the platform's (D251), and a
 * protected role only in an entity where the caller is its Entity Admin, read from the caller's
 * own answer acting in that entity.
 */
export const useGivableRoles = (entities: { id: string; name: string }[], enabled: boolean) => {
  const { userType } = useActiveSession();
  const isSuperAdmin = userType === SUPER_ADMIN;
  const places = [...(isSuperAdmin ? [null] : []), ...entities.map((entity) => entity.id)];
  const results = useQueries({
    queries: places.map((entityId) => ({
      queryKey: [PermissionsQueryKeys.getAllRoles, "in", entityId ?? "servengine"],
      queryFn: ({ signal }: { signal: AbortSignal }) => fetchRolesIn(entityId, signal),
      enabled,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const callerAccess = useQueries({
    queries: entities.map((entity) => ({
      ...effectiveAccessQueryOptions(entity.id),
      enabled: enabled && !isSuperAdmin,
      retry: false,
    })),
  });
  const mayGiveProtected = (entityId: string) =>
    isSuperAdmin ||
    callerAccess[entities.findIndex((entity) => entity.id === entityId)]?.data?.companies[entityId]
      ?.bypass === "entity_admin";

  const byKey = new Map<string, GivableRole>();
  results.forEach((result, index) => {
    const entityId = places[index];
    const entityName = entities.find((entity) => entity.id === entityId)?.name ?? "";
    for (const role of result.data ?? []) {
      if (entityId !== null && isProtectedRole(role) && !mayGiveProtected(entityId)) continue;
      const isEntityRole = role.level === "entity";
      const key = roleKey(role);
      if (entityId === null) {
        if (isEntityRole) continue;
        byKey.set(key, {
          key,
          name: role.name,
          roleId: role.id,
          level: role.level,
          scopeKinds: role.scopeKinds,
          copies: [],
        });
        continue;
      }
      if (!isEntityRole) continue;
      const entry = byKey.get(key) ?? {
        key,
        name: role.name,
        roleId: null,
        level: role.level,
        scopeKinds: null,
        copies: [],
      };
      entry.copies.push({ entityId, entityName, roleId: role.id, scopeKinds: role.scopeKinds });
      byKey.set(key, entry);
    }
  });

  return {
    roles: [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name)),
    // A failed read of the caller's own answer only hides protected roles, so it is not an error.
    isLoading: [...results, ...callerAccess].some((result) => result.isLoading),
    isError: results.some((result) => result.isError),
    /** The first refused read, so a 403 (no Roles & Permissions view) is told apart. */
    error: results.find((result) => result.isError)?.error ?? null,
  };
};

/** The scope options endpoint's maximum page size. */
const SCOPE_NAMES_PAGE_SIZE = 500;

/**
 * Names for the values the Scope card shows, one options read per entity and box that has a
 * live rule. Everything else in the person's scope is already named (clients are counted).
 */
export const useScopeValueNames = (
  wanted: Array<{ tenantId: string; box: CategoryBox }>,
): ScopeValueNames => {
  const results = useQueries({
    queries: wanted.map(({ tenantId, box }) => ({
      queryKey: ["scopeValueNames", tenantId, box],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        fetchScopeOptions({ tenantId, box, pageSize: SCOPE_NAMES_PAGE_SIZE }, signal),
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  });
  const names: ScopeValueNames = {};
  results.forEach((result, index) => {
    const { tenantId, box } = wanted[index];
    if (!result.data) return;
    names[tenantId] ??= {};
    names[tenantId][box] = Object.fromEntries(
      result.data.options.map((option) => [option.value, option.label]),
    );
  });
  return names;
};

export type ScopeStep = { company: UserScopeCompany; writes: ScopeWrite[] };

const grantRole = (userId: string, grant: RoleGrant) => {
  // A ServEngine-level role is granted at ServEngine level, never in an entity the sidebar has
  // picked, where the routes would not find it.
  const actingEntityId = grant.entityId ?? PLATFORM_TENANT_ID;
  return grant.endsOn
    ? addTemporaryRole(userId, { roleId: grant.roleId, endsOn: grant.endsOn }, actingEntityId)
    : changeUserRoles({ userId, addedRoleIds: [grant.roleId], actingEntityId });
};

export type AddRoleOutcome = {
  /** Every grant that has landed, this attempt or an earlier one. */
  granted: string[];
  failedGrants: number;
  /** The first refused grant's error, for its plain-words reason (409 held, 403 not allowed). */
  grantError: unknown;
  /** Entities whose scope was written in full this attempt. */
  savedScope: string[];
  failedScope: number;
};

/**
 * Add a Role with the scope it needs, in one save: each grant (an entity's own copy of the role,
 * or the ServEngine-level role), then each entity's scope once a grant reaches it. Every step is
 * attempted, and the result says what landed, so a retry sends only the grants and scope still
 * missing.
 */
export const useAddUserRoleWithScope = (userId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      grants,
      alreadyGranted,
      scope,
    }: {
      grants: RoleGrant[];
      alreadyGranted: string[];
      scope: ScopeStep[];
    }): Promise<AddRoleOutcome> => {
      const outcome: AddRoleOutcome = {
        granted: [...alreadyGranted],
        failedGrants: 0,
        grantError: null,
        savedScope: [],
        failedScope: 0,
      };
      for (const grant of grants) {
        if (outcome.granted.includes(grant.roleId)) continue;
        try {
          await grantRole(userId, grant);
          outcome.granted.push(grant.roleId);
        } catch (error) {
          outcome.failedGrants += 1;
          outcome.grantError ??= error;
        }
      }
      for (const step of scope) {
        if (!grantReaches(grants, outcome.granted, step.company.tenantId)) continue;
        const result = await saveEntityScope(userId, step.company, step.writes);
        if (result.failed.length > 0) outcome.failedScope += 1;
        else outcome.savedScope.push(step.company.tenantId);
      }
      return outcome;
    },
    onSettled: () => refreshPersonRecord(queryClient, userId, "roles"),
  });
};
